import React from "react";
import { useNavigate } from "react-router-dom";
import { useCampus } from "features/campus/hooks";
import { usePerfilSettings } from "features/perfil/settings/store";
import { useUpdateMiFamilia } from "features/perfil/familia/hooks";
import { useCreateHijo, useDeleteHijo, useMisHijos, useUpdateHijo } from "features/perfil/hijos/hooks";
import { usePerfilRegistroContext, useRegisterCamp, useRegisterClub } from "features/perfil/registro/hooks";
import PerfilLoadingPage from "../../../layouts/Perfil/PerfilLoadingPage";
import WakoHappy from "../../../assets/mascots/wako/Happy.svg";
import WakoPlaying from "../../../assets/mascots/wako/Playing.svg";
import TemporaryScreen from "./components/TemporaryScreen";
import StartStep from "./steps/StartStep";
import CampusStep from "./steps/CampusStep";
import SchoolStep from "./steps/SchoolStep";
import MotherStep from "./steps/MotherStep";
import FatherStep from "./steps/FatherStep";
import ChildrenStep from "./steps/ChildrenStep";
import RegistrationStep from "./steps/RegistrationStep";
import CompleteStep from "./steps/CompleteStep";
import {
  DEFAULT_CHILD, DRAFT_KEY, EMPTY_PARENT, SCHOOL_OPTIONS,
  formatMexicanPhone, parentComplete, readDraft,
} from "./config";

export default function OnboardingPerfil() {
  const navigate = useNavigate();
  const perfil = usePerfilSettings();
  const { data: campuses = [], isLoading: campusLoading } = useCampus();
  const { data: children = [], refetch: refetchChildren } = useMisHijos(perfil.ready);
  const { data: registration = {}, refetch: refetchRegistration } = usePerfilRegistroContext();
  const updateFamily = useUpdateMiFamilia();
  const createChild = useCreateHijo();
  const updateChild = useUpdateHijo();
  const deleteChild = useDeleteHijo();
  const registerClub = useRegisterClub();
  const registerCamp = useRegisterCamp();
  const saved = React.useMemo(readDraft, []);
  const [step, setStep] = React.useState(saved.step || 0);
  const [shared, setShared] = React.useState(saved.shared || { campusId: "", colegio: "" });
  const [customSchool, setCustomSchool] = React.useState(Boolean(saved.shared?.colegio && !SCHOOL_OPTIONS.includes(saved.shared.colegio)));
  const [parents, setParents] = React.useState({ madre: EMPTY_PARENT, padre: EMPTY_PARENT });
  const [childForm, setChildForm] = React.useState(DEFAULT_CHILD);
  const [editingId, setEditingId] = React.useState(null);
  const [childFormOpen, setChildFormOpen] = React.useState(false);
  const [choices, setChoices] = React.useState(saved.choices || {});
  const [error, setError] = React.useState("");
  const [mascotMoment, setMascotMoment] = React.useState(null);
  const socios = registration.socios || [];
  const busy = updateFamily.isPending || createChild.isPending || updateChild.isPending || deleteChild.isPending || registerClub.isPending || registerCamp.isPending;
  const registrationReady = socios.length > 0 && socios.every((socio) => {
    const choice = choices[socio.id] || {};
    const clubSelected = socio.club.registered || choice.club !== false;
    const campSelected = socio.camp.registered || (socio.camp.eligible && Boolean(choice.camp));
    return clubSelected || campSelected;
  });
  const showChildForm = childFormOpen || Boolean(editingId) || children.length === 0;
  const emptyChildWithFamilyNames = () => ({
    ...DEFAULT_CHILD,
    apellidoPaterno: parents.padre.apellidoPaterno.trim(),
    apellidoMaterno: parents.madre.apellidoPaterno.trim(),
  });

  React.useEffect(() => {
    const family = perfil.familia;
    if (!family) return;
    setParents({
      madre: { nombre: family.nombreMama || "", apellidoPaterno: family.apellidoPaternoMama || "", apellidoMaterno: family.apellidoMaternoMama || "", telefono: formatMexicanPhone(family.telefonoMama || ""), correo: family.correoMama || "" },
      padre: { nombre: family.nombrePapa || "", apellidoPaterno: family.apellidoPaternoPapa || "", apellidoMaterno: family.apellidoMaternoPapa || "", telefono: formatMexicanPhone(family.telefonoPapa || ""), correo: family.correoPapa || "" },
    });
  }, [perfil.familia]);

  React.useEffect(() => {
    if (editingId) return;
    setChildForm((current) => ({
      ...current,
      apellidoPaterno: current.apellidoPaterno || parents.padre.apellidoPaterno.trim(),
      apellidoMaterno: current.apellidoMaterno || parents.madre.apellidoPaterno.trim(),
    }));
  }, [editingId, parents.padre.apellidoPaterno, parents.madre.apellidoPaterno]);

  React.useEffect(() => {
    if (!shared.campusId && perfil.familia?.campusId) setShared((current) => ({ ...current, campusId: perfil.familia.campusId, colegio: current.colegio || perfil.familia.colegio || "" }));
    else if (!shared.campusId && children[0]) setShared({ campusId: children[0].campusId, colegio: children[0].colegio || "" });
    else if (!shared.campusId && campuses[0]) setShared((current) => ({ ...current, campusId: campuses[0].id }));
    else if (!shared.colegio && perfil.familia?.colegio) setShared((current) => ({ ...current, colegio: perfil.familia.colegio }));
  }, [campuses, children, perfil.familia, shared.campusId, shared.colegio]);

  React.useEffect(() => {
    if (shared.colegio && !SCHOOL_OPTIONS.includes(shared.colegio)) setCustomSchool(true);
  }, [shared.colegio]);

  React.useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, shared, choices }));
  }, [step, shared, choices]);

  const go = (nextStep) => {
    setError("");
    setStep(nextStep);
    document.getElementById("scrollableBody")?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveParents = async () => {
    try {
      await updateFamily.mutateAsync({
        campusId: shared.campusId, colegio: shared.colegio.trim(),
        nombreMama: parents.madre.nombre.trim(), apellidoPaternoMama: parents.madre.apellidoPaterno.trim(), apellidoMaternoMama: parents.madre.apellidoMaterno.trim(), telefonoMama: parents.madre.telefono.trim(), correoMama: parents.madre.correo.trim(),
        nombrePapa: parents.padre.nombre.trim(), apellidoPaternoPapa: parents.padre.apellidoPaterno.trim(), apellidoMaternoPapa: parents.padre.apellidoMaterno.trim(), telefonoPapa: parents.padre.telefono.trim(), correoPapa: parents.padre.correo.trim(),
      });
      await perfil.refetchContext();
      setChildForm((current) => ({ ...current, apellidoPaterno: current.apellidoPaterno || parents.padre.apellidoPaterno.trim(), apellidoMaterno: current.apellidoMaterno || parents.madre.apellidoPaterno.trim() }));
      setMascotMoment("parents");
    } catch { setError("No pudimos guardar los contactos. Revisa la informaci\u00f3n."); }
  };

  const saveChild = async () => {
    const payload = { ...childForm, nombre: childForm.nombre.trim(), apellidoPaterno: childForm.apellidoPaterno.trim(), apellidoMaterno: childForm.apellidoMaterno.trim(), colegio: shared.colegio.trim(), campusId: shared.campusId, generacion: Number(childForm.generacion) };
    try {
      if (editingId) await updateChild.mutateAsync({ id: editingId, patch: payload });
      else await createChild.mutateAsync(payload);
      await refetchChildren();
      await refetchRegistration();
      setEditingId(null);
      setChildFormOpen(false);
      setChildForm(emptyChildWithFamilyNames());
      setError("");
    } catch { setError("No pudimos guardar al hijo. Verifica el grado y el campus."); }
  };

  const editChild = (child) => {
    setEditingId(child.id);
    setChildFormOpen(true);
    setChildForm({ nombre: child.nombre || "", apellidoPaterno: child.apellidoPaterno || "", apellidoMaterno: child.apellidoMaterno || "", fechaNacimiento: child.fechaNacimiento || "", generacion: Number(child.generacion) });
    setError("");
  };

  const closeChildForm = () => {
    setEditingId(null);
    setChildFormOpen(false);
    setChildForm(emptyChildWithFamilyNames());
  };

  const removeChild = async (id) => {
    try {
      await deleteChild.mutateAsync(id);
      await refetchChildren();
      await refetchRegistration();
      if (editingId === id) closeChildForm();
    } catch { setError("No pudimos eliminar al hijo."); }
  };

  const enroll = async () => {
    try {
      for (const socio of socios) {
        const choice = choices[socio.id] || {};
        if (!socio.club.registered && choice.club !== false) await registerClub.mutateAsync({ socioId: socio.id });
        if (socio.camp.eligible && !socio.camp.registered && choice.camp) await registerCamp.mutateAsync({ socioId: socio.id, talla: choice.talla || "nino_m" });
      }
      await refetchRegistration();
      go(7);
    } catch { setError("Un registro no pudo completarse. Lo que ya guardaste sigue seguro."); }
  };

  const finish = () => {
    localStorage.removeItem(DRAFT_KEY);
    navigate("/perfil", { replace: true });
  };

  if (!perfil.ready || campusLoading) return <PerfilLoadingPage message="Preparando tu registro..." description="Estamos reuniendo la información de tu familia." />;

  if (mascotMoment === "parents") return <TemporaryScreen image={WakoHappy} eyebrow="Información guardada" title="¡Ya están un paso más cerca!" description="Los datos de mamá y papá quedaron guardados. Ahora agreguemos a sus hijos." onDone={() => { setMascotMoment(null); go(5); }}/>;

  if (mascotMoment === "children") return <TemporaryScreen image={WakoPlaying} eyebrow="Información guardada" title="¡Ya casi terminan!" description="Guardamos la información de sus hijos. Solo faltan sus registros." onDone={() => { setMascotMoment(null); go(6); }}/>;

  if (step === 0) return <StartStep onStart={() => go(1)}/>;
  if (step === 1) return <CampusStep campuses={campuses} campusId={shared.campusId} onBack={() => go(0)} onSelect={(campusId) => { setShared({ ...shared, campusId }); window.setTimeout(() => go(2), 180); }}/>;
  if (step === 2) return <SchoolStep school={shared.colegio} customSchool={customSchool} setCustomSchool={() => { if (!customSchool && SCHOOL_OPTIONS.includes(shared.colegio)) setShared({ ...shared, colegio: "" }); setCustomSchool(true); }} onChange={(colegio) => setShared({ ...shared, colegio })} onQuickSelect={(colegio) => { setCustomSchool(false); setShared({ ...shared, colegio }); window.setTimeout(() => go(3), 180); }} onBack={() => go(1)} onNext={() => go(3)}/>;
  if (step === 3) return <MotherStep parents={parents} setParents={setParents} formatPhone={formatMexicanPhone} complete={parentComplete(parents.madre)} onBack={() => go(2)} onNext={() => go(4)}/>;
  if (step === 4) return <FatherStep parents={parents} setParents={setParents} formatPhone={formatMexicanPhone} complete={parentComplete(parents.padre)} error={error} busy={busy} onBack={() => go(3)} onSave={saveParents}/>;
  if (step === 5) return <ChildrenStep children={children} showForm={showChildForm} editingId={editingId} childForm={childForm} setChildForm={setChildForm} error={error} busy={busy} onBack={() => go(4)} onContinue={() => setMascotMoment("children")} onSave={saveChild} onEdit={editChild} onDelete={removeChild} onAdd={() => { setEditingId(null); setChildForm(emptyChildWithFamilyNames()); setChildFormOpen(true); }} onCloseForm={closeChildForm}/>;
  if (step === 6) return <RegistrationStep socios={socios} choices={choices} setChoices={setChoices} ready={registrationReady} busy={busy} error={error} onBack={() => go(5)} onConfirm={enroll}/>;
  return <CompleteStep onFinish={finish}/>;
}
